import type { Catalog, ProductAttributesTypeColorPlp, ProductCardExtended, ProductFilters, ProductGalleryImage } from '#root/api/clients/product/data-contracts'

interface SearchResult {
  image: string
  pageUrl: string
  Currency: string
  ListPrice: number
  Price: number
  productId: string
  ProductName: string
}

interface AgentProductsPayload {
  searchResults: SearchResult[]
  showMore: boolean
  description?: string
  currentOffset?: number
  total?: number
  searchQuery?: string
  filterFacets?: ProductFilters
  sortOptions?: Catalog['sortOptions']
}

function mapToProductCardExtended(item: SearchResult): ProductCardExtended {
  const gallery: ProductGalleryImage[] = item.image
    ? [{ type: 'image' as const, src: item.image, alt: item.ProductName }]
    : []

  const attributes: ProductAttributesTypeColorPlp[] = [{
    label: 'Color',
    type: 'color',
    options: [],
  }]

  return {
    id: item.productId,
    name: item.ProductName,
    url: item.pageUrl,
    price: {
      lowCurrent: item.Price,
      highCurrent: item.Price,
      lowOriginal: item.ListPrice,
      highOriginal: item.ListPrice,
    },
    currency: item.Currency || 'USD',
    gallery,
    attributes,
  }
}

export const useAgentProductsStore = defineStore('agentProducts', () => {
  const products = ref<ProductCardExtended[]>([])
  const description = ref('')
  const searchQuery = ref('')
  const active = ref(false)
  const showMore = ref(false)
  const currentOffset = ref(0)
  const pageSize = ref(0)
  const totalProducts = ref(0)
  const filters = ref<Record<string, any> | undefined>()
  const filterFacets = ref<ProductFilters>([])
  const sortOptions = ref<Catalog['sortOptions']>()
  const agentTriggeredPDP = ref(false)

  function setProducts(payload: AgentProductsPayload) {
    products.value = payload.searchResults.map(mapToProductCardExtended)
    description.value = payload.description || ''
    searchQuery.value = payload.searchQuery || payload.description || ''
    showMore.value = payload.showMore
    pageSize.value = payload.searchResults.length
    currentOffset.value = payload.searchResults.length
    totalProducts.value = payload.total ?? payload.searchResults.length
    if (payload.filterFacets) filterFacets.value = payload.filterFacets
    if (payload.sortOptions) sortOptions.value = payload.sortOptions
    active.value = true
  }

  function appendProducts(payload: AgentProductsPayload) {
    const mapped = payload.searchResults.map(mapToProductCardExtended)
    products.value = [...products.value, ...mapped]
    showMore.value = payload.showMore
    currentOffset.value += mapped.length
  }

  function clearProducts() {
    products.value = []
    description.value = ''
    searchQuery.value = ''
    active.value = false
    showMore.value = false
    currentOffset.value = 0
    pageSize.value = 0
    totalProducts.value = 0
    filters.value = undefined
    filterFacets.value = []
    sortOptions.value = undefined
    agentTriggeredPDP.value = false
  }

  return {
    products,
    description,
    searchQuery,
    active,
    showMore,
    currentOffset,
    pageSize,
    totalProducts,
    filters,
    filterFacets,
    sortOptions,
    agentTriggeredPDP,
    setProducts,
    appendProducts,
    clearProducts,
  }
})
